/**
 * Safely opens a Shopify Admin URL from within an embedded app iframe.
 *
 * App Bridge 4 patches `window.open` inside the app iframe and often turns
 * admin.shopify.com + `_blank` into same-window host navigation. To keep
 * Freebi open and load the theme editor in a real new tab we:
 *  1) Prefer `window.top.open` / `parent.open` (host context, not patched)
 *  2) Fall back to a programmatic &lt;a target="_blank"&gt; click
 *  3) Fall back to iframe window.open
 *  4) Last resort: same-window `_top`
 */

/**
 * Open a URL in a new browser tab from an embedded Shopify admin app.
 */
export function openInNewBrowserTab(url: string): void {
  if (!url || url === '#') return;

  // 1) Host window open — App Bridge only patches the app iframe's window.open
  try {
    const topWin = window.top;
    if (topWin && topWin !== window) {
      const w = topWin.open(url, '_blank', 'noopener,noreferrer');
      if (w) {
        try {
          w.opener = null;
        } catch {
          /* ignore */
        }
        return;
      }
    }
  } catch {
    /* cross-origin top — try parent */
  }

  try {
    if (window.parent && window.parent !== window) {
      const w = window.parent.open(url, '_blank', 'noopener,noreferrer');
      if (w) {
        try {
          w.opener = null;
        } catch {
          /* ignore */
        }
        return;
      }
    }
  } catch {
    /* ignore */
  }

  // 2) Programmatic anchor click (same user gesture)
  try {
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.setAttribute('referrerpolicy', 'no-referrer');
    document.body.appendChild(a);
    a.click();
    a.remove();
    return;
  } catch {
    /* fall through */
  }

  // 3) Iframe window.open (may be rewritten by App Bridge for admin URLs)
  try {
    const w = window.open(url, '_blank', 'noopener,noreferrer');
    if (w) {
      try {
        w.opener = null;
      } catch {
        /* ignore */
      }
      return;
    }
  } catch {
    /* fall through */
  }

  // 4) Last resort — navigate host (same window)
  window.open(url, '_top');
}

/**
 * @param newTab - When true, force a new browser tab (Activate app / theme editor).
 *                 When false/omitted, navigate the Shopify Admin host (`_top`).
 */
export function openShopifyAdminUrl(
  url: string,
  opts?: { newTab?: boolean },
) {
  if (!url || url === '#') {
    // eslint-disable-next-line no-console
    console.warn('[Freebi] openShopifyAdminUrl: no URL provided');
    return;
  }

  // eslint-disable-next-line no-console
  console.log(
    '[Freebi] Navigating to:',
    url,
    opts?.newTab ? '(new tab)' : '(same window)',
  );

  if (opts?.newTab) {
    openInNewBrowserTab(url);
    return;
  }

  // Same-window admin navigation (App Bridge intercepts _top)
  window.open(url, '_top');
}
